'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';

import * as React from 'react';
import { cn } from '@/utils/cn';
import { Icon } from '../../entities/Sprite';

function Accordion({
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('border-b last:border-b-0', className)}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  iconClosed,
  iconOpened,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  iconClosed?: React.ReactNode;
  iconOpened?: React.ReactNode;
}) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-md font-medium transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=closed]>span]:hidden [&[data-state=open]>svg]:hidden',
          className,
        )}
        {...props}
      >
        {children}
        {iconClosed || (
          <Icon
            name="plus"
            className="text-white pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200 -mt-1"
          />
        )}
        <span>
          {iconOpened || (
            <Icon
              name="minus"
              className="text-white pointer-events-none size-6 -mr-1 -mt-1 shrink-0 translate-y-0.5 transition-transform duration-200"
            />
          )}
        </span>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  rootClassName,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content> & {
  rootClassName?: string;
}) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={cn('overflow-hidden text-sm', rootClassName)}
      {...props}
    >
      <div className={cn('pt-0', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
